<template>
  <div class="chat">
    <header>
      <h2>Live chat</h2>
      <div class="manage-chat" @click.prevent.stop="showManage = !showManage">
        <i class="icon-settings-gear-24" />
        <span>Manage</span>
        <div class="manage-options" v-click-outside="clickedOutsideManage" v-if="showManage">
          <ul>
            <li @click="chatMessage.update = 'welcome'; showManage = false">Set welcome message</li>
            <li @click="chatMessage.update = 'pinned'; showManage = false">Set pinned message</li>
            <li @click.prevent.stop="showUserlist = !showUserlist; showManage = false;">{{ showUserlist ? 'Hide' : 'Show' }} userlist</li>
          </ul>
        </div>
      </div>
    </header>
    <div class="chat-inner">
      <div class="user-list nice-scrollbar" :data-show="showUserlist">
        <template v-for="user in userlist">
          <div class="user" :key="user.user_id" v-if="user.name !== ''">
            <span>{{ user.name }}</span>
            <p v-if="user.status !== 'banned'" @click="banUser(user, true)">Ban</p>
            <p v-if="user.status === 'banned'" @click="banUser(user, false)">Unban</p>
          </div>
        </template>
      </div>
    <div class="chat-messages nice-scrollbar" ref="messages">
      <div
        class="chat-message"
        v-for="(message, index) in messages"
        :key="index"
        :data-type="message.event"
        :data-manageable="!!message.name && !message.moderator"
        :data-moderator="message.moderator"
        :data-managing="manageMessage === message.message_id"
        @click.prevent.stop="handleMessage(message)"
      >
        <div class="inner">
        <div class="timestamp" :title="message.timestamp | formatdate('YYYY-MM-DD HH:mm')">{{ message.timestamp | timestamp('HH:mm') }}</div>
        <div class="user">
          {{ message.name ? message.name.substring(0, 15) : '' }}
        </div>
        <div class="text">
          <template v-if="message.type === 'reply'">
            <span class="reply-to">@{{ message.reply_to.name }}</span>:
          </template>
          <template v-if="['welcome', 'pinned', 'chat.product', 'form.published', 'form.promoted', 'form.completed', 'giveaway.published', 'giveaway.completed', 'giveaway.closed'].includes(message.event)">
            <div class="text-icon">
              <template v-if="message.event === 'welcome'">
                <i class="icon-information-24" />
              </template>
              <template v-else-if="message.event === 'pinned'">
                <i class="icon-pin" />
              </template>
              <template v-else-if="['form.published', 'form.completed', 'form.promoted'].includes(message.event)">
                <i class="icon-form-pen" />
              </template>
              <template v-else-if="['giveaway.published', 'giveaway.completed', 'giveaway.closed'].includes(message.event)">
                <i class="icon-gift-24" />
              </template>
              <template v-else-if="message.event === 'chat.product'">
                <img :src="`https://${broadcast.fqdn}/images/h/36/${message.product.image_url}`" />
              </template>
            </div>
          </template>
          {{ message.message }}
          <template v-if="message.event === 'welcome' && message.message === ''">
            <span style="color: #818181">Welcome message removed</span>
          </template>
          <template v-if="message.event === 'pinned' && message.message === ''">
            <span style="color: #818181">Pinned message removed</span>
          </template>
        </div>
        </div>
        <div class="manage-message" v-if="manageMessage === message.message_id" v-click-outside="clickedOutsideChatManage">
          <ul>
            <template v-if="!message.moderator && !['welcome', 'pinned', 'chat.product', 'form.published', 'form.promoted', 'form.completed', 'giveaway.published', 'giveaway.completed', 'giveaway.closed'].includes(message.event)">
              <li class="blue link" @click.prevent.stop="replyTo(message)">Reply to</li>
              <li class="blue link" v-if="getUserObject(message.user_id).status !== 'banned'" @click.prevent.stop="banUser(getUserObject(message.user_id), true)">Ban</li>
              <li class="blue link" v-if="getUserObject(message.user_id).status === 'banned'" @click.prevent.stop="banUser(getUserObject(message.user_id), false)">Unban</li>
            </template>
            <li class="blue link" @click.prevent.stop="deleteMessage(message.message_id, message.moderator)">Remove message</li>
          </ul>
        </div>
      </div>
    </div>
    </div>
    <div class="interact">
      <template v-if="nickname === '' || changeNicknameState">
        <form class="d-f f-w" @submit.prevent="changeNickname">
          <FormInput type="text" class="f-w with-button" v-model="nicknameInput" placeholder="Choose a nickname" />
          <Button class="no-wrap with-input no-border-radius">Set nickname</Button>
        </form>
      </template>
      <template v-else>
        <form class="d-f f-w" @submit.prevent="sendMessage">
          <FormInput type="text" class="f-w with-button dots-padding" v-model="message" :placeholder="`Chat as @${nickname}`" />
          <div class="chat-actions">
            <div class="manage-interact" v-if="manageInteract" v-click-outside="clickedOutsideManageInteract">
              <ul>
                <li @click="changeNicknameState = true; manageInteract = false;">Change nickname</li>
              </ul>
            </div>
            <i class="icon-dots-tight-24" @click.prevent.stop="manageInteract = !manageInteract" />
          </div>
        </form>
      </template>
    </div>
    <portal to="modals">
      <modal :show="chatMessage.update !== ''" @close="chatMessage.update = ''" class="auto-height" :title="`Set ${chatMessage.update === 'pinned' ? 'pinned' : 'welcome'} message`">
        <div class="p-a-4">
          <form class="d-f" @submit.prevent="updateMessage(chatMessage.update)">
            <FormInput type="text" class="f-w" ref="setMessage" v-model="chatMessage[chatMessage.update]" :placeholder="chatMessage.update === 'pinned' ? 'Enter pinned message' : 'Enter welcome message'" />
          </form>
        </div>
        <template v-slot:footer>
          <div class="d-f f-w">
            <Button class="no-wrap" @click="updateMessage(chatMessage.update)">Set message</Button>
            <Button @click="chatMessage.update = ''" class="red outline ml-a">Cancel</Button>
          </div>
        </template>
      </modal>
      <modal :show="replyToMessage !== null" @close="replyToMessage = null" class="auto-height" :title="`Reply to message ${replyToMessage ? `by @${replyToMessage.name}` : ''}`" :subtitle="replyToMessage ? replyToMessage.message : ''">
        <div class="p-a-4">
          <form class="d-f" @submit.prevent="replyMessage">
            <FormInput type="text" class="f-w" ref="replyMessage" v-model="replyToAnswer" placeholder="Write a response" />
          </form>
        </div>
        <template v-slot:footer>
          <div class="d-f f-w">
            <Button class="no-wrap" @click="replyMessage">Reply</Button>
            <Button @click="replyToAnswer = ''; replyToMessage = null;" class="red outline ml-a">Cancel</Button>
          </div>
        </template>
      </modal>
    </portal>
  </div>
</template>

<script>

import DOMPurify from 'dompurify';
import { ChatService } from '@/services/chat.service';

export default {
  computed: {
    /**
     * Return the current broadcast in store
     * @returns {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    /**
     * Return the current messages in socket
     * @returns {Array}
     */
    messages() {
      return this.$store.getters['Chat/messages'](this.broadcast.id);
    },
    /**
     * Return the current nickname in store
     * @returns {String}
     */
    nickname() {
      return this.$store.getters['Chat/chatname'];
    },
    validNickname() {
      return this.nicknameInput.trim().length >= 3;
    },
    userlist() {
      return this.$store.getters['Chat/userlist'](this.broadcast.id);
    },
    /**
     * Get the current pinned message
     * @returns {?Object}
     */
    pinnedMessages() {
      return this.$store.getters['Chat/pinnedMessage'](this.broadcast.id);
    },
  },
  methods: {
    handleMessage(message) {
      switch (message.event) {
        case 'welcome':
        case 'pinned':
          this.chatMessage.update = message.event;
          break;
        case 'form.published':
        case 'form.completed':
        case 'form.promoted':
          this.$store.dispatch('Forms/getForm', { id: message.payload_id });
          break;
        case 'giveaway.published':
        case 'giveaway.completed':
        case 'giveaway.closed':
          this.$store.dispatch('Giveaway/getGiveaway', { id: message.payload_id });
          break;
        case 'chat.product':
          break;
        default:
          if (this.manageMessage !== message.message_id) {
            this.manageMessage = message.message_id;
          } else {
            this.manageMessage = '';
          }
          break;
      }
    },
    getUserObject(id) {
      return this.userlist.find(user => user.user_id === id) || {};
    },
    async changeNickname() {
      if (!this.validNickname) {
        return;
      }

      await this.$store.dispatch('Events/send', {
        id: this.broadcast.id,
        payload: {
          event: 'chat.user',
          payload: {
            name: this.nicknameInput,
          },
        },
      });
      await this.$store.dispatch('Chat/setChatname', this.nicknameInput);
      this.changeNicknameState = false;
    },
    async updateMessage(type) {
      const message = this.chatMessage[type];
      await ChatService.setMessage(this.broadcast.id, type, DOMPurify.sanitize(message, { ALLOWED_TAGS: [] }));

      this.chatMessage.update = '';
    },
    sendMessage() {
      ChatService.sendMessage(this.broadcast.id, {
        message: this.message,
        name: this.nickname,
      });

      this.message = '';
    },
    messagesScrollDown() {
      if (!this.$refs.messages) {
        return;
      }

      if (this.messagesScrollLock) {
        return;
      }

      this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
    },
    scrollEvent() {
      if (!this.$refs.messages) {
        return;
      }

      const {
        messages: {
          offsetHeight,
          scrollTop,
          scrollHeight,
        },
      } = this.$refs;

      if (offsetHeight + scrollTop >= scrollHeight) {
        this.messagesScrollLock = false;
        return;
      }

      this.messagesScrollLock = true;
    },
    clickedOutsideManage() {
      this.showManage = false;
    },
    clickedOutsideChatManage() {
      this.manageMessage = '';
    },
    clickedOutsideManageInteract() {
      this.manageInteract = false;
    },
    async banUser(user, ban) {
      const bool = await swal({
        title: 'Are you sure?',
        text: ban ? `This will delete all messages from the user "${user.name}" and the user will not be able to chat anymore.` : `This will let the user "${user.name}" write chat messages again.`,
        icon: 'warning',
        buttons: true,
        dangerMode: true,
      });

      if (!bool) {
        return;
      }

      if (ban) {
        await ChatService.banUser(this.broadcast.id, user.session_id);
      } else {
        await ChatService.unbanUser(this.broadcast.id, user.session_id);
      }
    },
    /**
     * Delete message
     * @param {String} id
     */
    async deleteMessage(id, moderator) {
      const bool = await swal({
        title: 'Are you sure?',
        text: 'This will delete this message and it cannot be reversed.',
        icon: 'warning',
        buttons: true,
        dangerMode: true,
      });

      if (!bool) {
        return;
      }

      ChatService.deleteMessage(this.broadcast.id, id, moderator);
    },
    replyTo(message) {
      this.replyToMessage = message;
    },
    replyMessage() {
      const {
        replyToMessage,
        replyToAnswer,
        nickname,
        broadcast: {
          id,
        },
      } = this;

      if (replyToAnswer.trim() === '') {
        return;
      }

      ChatService.replyMessage(id, replyToMessage.message_id, {
        message: replyToAnswer,
        name: nickname,
      });

      this.replyToAnswer = '';
      this.replyToMessage = null;
    },
  },
  watch: {
    messages: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.messagesScrollDown();
        });
      },
    },
    pinnedMessages: {
      immediate: true,
      handler(messages) {
        if (!messages) {
          return;
        }

        const { pinned = '', welcome = '' } = messages;
        this.chatMessage.pinned = pinned;
        this.chatMessage.welcome = welcome;
      },
    },
  },
  data() {
    return {
      replyToAnswer: '',
      replyToMessage: null,
      messagesScrollLock: false,
      changeNicknameState: false,
      nicknameInput: '',
      message: '',
      showManage: false,
      showUserlist: false,
      manageMessage: '',
      manageInteract: false,
      chatMessage: {
        update: '',
        pinned: '',
        welcome: '',
      },
    };
  },
  mounted() {
    this.$store.dispatch('Chat/getUserlist', {
      id: this.broadcast.id,
      interval: ['ready', 'liveStarting', 'live', 'liveStopping'].includes(this.broadcast.status.life_cycle_status),
    });

    this.$refs.messages.addEventListener('scroll', this.scrollEvent);
    window.addEventListener('resize', this.messagesScrollDown);

    this.nicknameInput = this.nickname;
  },
  beforeDestroy() {
    this.$store.dispatch('Chat/clearUserlist');
    this.$refs.messages.removeEventListener('scroll', this.scrollEvent);
    window.removeEventListener('resize', this.messagesScrollDown);
  },
};
</script>

<style lang="scss" scoped>
.chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .chat-inner {
    flex: 1;
    overflow: hidden;
    display: flex;
    position: relative;
  }

  .user-list {
    background: white;
    box-shadow: inset 0 1px 2px 0 rgba(0, 0, 0, .10);
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    transform: translateX(100%);
    transition: transform .3s;
    width: 200px;
    overflow: auto;
    border-left: 1px solid #C1C5D7;

    .user {
      border-bottom: 1px solid #F3F5F9;
      display: flex;
      font-size: 12px;
      padding: 4px 12px;

      span {
        font-weight: 600;
      }

      p {
        color: #D0021B;
        margin-left: auto;
        cursor: pointer;
      }
    }

    &[data-show="true"] {
      transform: translateX(0);
    }
  }

  header {
    align-items: center;
    display: flex;
    padding: 0 16px;
    height: 50px;
    z-index: 2;

    h2 {
      color: #757882;
      font-weight: 500;
    }

    .manage-chat {
      align-items: center;
      display: flex;
      margin-left: auto;
      position: relative;
      cursor: pointer;

      & > i {
        color: #0060FF;
        font-size: 18px;
      }

      span {
        color: #0060FF;
        font-size: 12px;
        font-weight: 500;
        margin-left: 6px;
      }

      .manage-options {
        border: 1px solid #C1C5D7;
        border-radius: 5px;
        position: absolute;
        right: 0;
        top: 100%;
        width: 150px;

        li {
          background-color: white;
          color: #0060FF;
          font-size: 12px;
          font-weight: 500;
          padding: 8px 8px;

          &:not(:last-child) {
            border-bottom: 1px solid #F3F5F9;
          }

          &:first-child {
            border-radius: 5px 5px 0 0;
          }

          &:last-child {
            border-radius: 0 0 5px 5px;
          }

          &:only-child {
            border-radius: 5px;
          }

          &:hover {
            background-color: #F3F5F9;
            cursor: pointer;
          }
        }
      }
    }
  }

  .chat-messages {
    background: #F3F5F9;
    box-shadow: inset 0 1px 2px 0 rgba(0, 0, 0, .10);
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;

    .chat-message {

      &:first-child {
        margin-top: 4px;
      }

      &:last-child {
        margin-bottom: 4px;
      }

      &:hover {
        background-color: white;
        cursor: pointer;
      }

      .inner {
        display: flex;
        padding: 2px 8px;
      }

      &[data-managing="true"] {
        background-color: rgba(255, 255, 255, 0.687);
      }

      &[data-manageable="true"] {
        cursor: pointer;

        &:hover {
          background-color: rgba(255, 255, 255, 0.687);
        }
      }

      .manage-message {
        font-size: 12px;

        ul {
          padding: 4px 8px;

          li {
            &:hover {
              cursor: pointer;
              text-decoration: underline;
            }
          }
        }
      }

      .timestamp {
        color: #757882;
        font-size: 11px;
        font-family: Menlo, Consolas, monospace;
        padding-right: 5px;
        padding-top: 0px;
      }

      .user {
        font-weight: 600;
        font-size: 12px;
        padding-right: 3px;
        align-self: flex-start;
        white-space: nowrap;
      }

      .text {
        font-size: 12px;
        word-break: break-all;

        span.reply-to {
          color: #525357;
          font-weight: 500;
          display: inline-block;
        }
      }

      &[data-type="welcome"],
      &[data-type="pinned"],
      &[data-type^="form."],
      &[data-type="chat.product"],
      &[data-type^="giveaway."] {
        align-items: center;
        font-weight: 600;

        .text {
          background: #FFFFFF;
          box-shadow: 0 0 0 1px #D4D4D4;
          border-radius: 4px;
          display: flex;
          align-items: center;
          padding-right: 8px;

          .text-icon {
            color: white;
            align-items: center;
            justify-content: center;
            display: flex;
            height: 100%;
            min-height: 36px;
            width: 36px;
            margin-right: 8px;
            border-radius: 4px 0 0 4px;
            min-width: 36px;
            i {
              font-size: 20px;
            }
          }
        }

        &[data-type="welcome"], &[data-type="pinned"] {
          .text {
            .text-icon {
              background-color: #FFA850;
            }
          }
        }

        &[data-type^="form."] {
          .text {
            .text-icon {
              background-color: #ff7300;
            }
          }
        }

        &[data-type^="giveaway."] {
          .text {
            .text-icon {
              background-color: #FFD3FA;
              color: #C200AB;
            }
          }
        }

        &[data-type="chat.product"] {
          .text {
            .text-icon {
              width: auto;
              border-radius: 4px 0 0 4px;
              overflow: hidden;
            }
          }
        }
      }
    }
  }

  .interact {
    display: flex;
    position: relative;
    border-radius: 0 0 10px 10px;

    .chat-actions {
      position: absolute;
      right: 15px;
      top: 50%;
      transform: translateY(-50%);

      i {
        font-size: 18px;
        cursor: pointer;
      }
    }

    .manage-interact {
      border: 1px solid #C1C5D7;
      border-radius: 5px;
      position: absolute;
      right: 0;
      bottom: 100%;
      width: 150px;

      li {
        background-color: white;
        color: #0060FF;
        font-size: 12px;
        font-weight: 500;
        padding: 8px 8px;

        &:not(:last-child) {
          border-bottom: 1px solid #F3F5F9;
        }

        &:first-child {
          border-radius: 5px 5px 0 0;
        }

        &:last-child {
          border-radius: 0 0 5px 5px;
        }

        &:only-child {
          border-radius: 5px;
        }

        &:hover {
          background-color: #F3F5F9;
          cursor: pointer;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.chat {
  .interact {
    .input, input {
      border-radius: 0 0 10px 10px;
    }
  }
}
</style>
